import { formatDurationToDayHHMM, formatSecondsToHHMMSS } from "@utils/utils";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";

import { CalendarDateRange, Column, SearchableDropdownItem } from "@fronterahealth/frontera-ui-components";

import { useGetRbtVideoUploadTimeRawDataQuery } from "@api/graphql/types-and-hooks";
import { DialogFilterBar } from "@pages/VideoAnalytics/components/DialogFilterBar";
import { RawDataTableWrapper } from "@pages/VideoAnalytics/components/RawDataTableWrapper";
import { DATE_FORMAT, PAGE_SIZE } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

interface AvgVideoUploadTimeRawDataProps {}

interface AvgVideoUploadTimeRawDataType {
  rbtName: string;
  sessionDate: string;
  sessionDatetime: string;
  sessionDuration: number;
  uploadCompletionTimeInSeconds: number;
}
export const AvgVideoUploadTimeRawData: React.FC<AvgVideoUploadTimeRawDataProps> = () => {
  const { selectedRBTDates, selectedRBTs } = useVideoAnalytics();

  const [selectedRBT, setSelectedRBT] = useState<SearchableDropdownItem[]>(selectedRBTs);
  const [dates, setDates] = useState<CalendarDateRange | undefined>(selectedRBTDates);

  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  useEffect(() => {
    setPageNo(1);
  }, [dates, selectedRBT]);

  const { data, isFetching } = useGetRbtVideoUploadTimeRawDataQuery(
    {
      fromDate: dates?.startDate ? format(dates.startDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      toDate: dates?.endDate ? format(dates.endDate, DATE_FORMAT) : format(new Date(), DATE_FORMAT),
      rbtProviderIds: selectedRBT.map((item) => item.id),
      offset: pageNo * pageSize - pageSize,
      count: pageSize,
    },
    {
      queryKey: ["getRbtVideoUploadTimeRawData", dates, selectedRBT, pageNo, pageSize],
    },
  );
  const columns: Column<AvgVideoUploadTimeRawDataType>[] = [
    {
      title: "RBT",
      key: "rbtName",
      dataIndex: "rbtName",
      render: (value) => value || "-",
    },
    {
      title: "Session Date & Time",
      key: "sessionDatetime",
      dataIndex: "sessionDatetime",
      render: (value) => {
        if (value) {
          const parsedDate = parseISO(value as string);
          const formattedDate = format(parsedDate, "yyyy-MM-dd HH:mm");
          return formattedDate;
        }
        return "-";
      },
    },
    {
      title: "Session Duration",
      key: "sessionDuration",
      dataIndex: "sessionDuration",
      render: (value: string | number) => (value ? formatSecondsToHHMMSS(value as number) : "-"),
    },
    {
      title: "Upload Time",
      key: "uploadCompletionTimeInSeconds",
      dataIndex: "uploadCompletionTimeInSeconds",
      render: (value: string | number) => (value ? formatDurationToDayHHMM(value as number) : "-"),
    },
  ];
  return (
    <div className="grid space-y-2">
      <DialogFilterBar
        dates={dates}
        setDates={setDates}
        selectedRBTs={selectedRBT}
        setSelectedRBTs={setSelectedRBT}
        showBCBAFilter={false}
        showClientFilter={false}
      />
      <RawDataTableWrapper
        data={
          data && data.getRbtVideoUploadTimeRawData?.rawData
            ? (data.getRbtVideoUploadTimeRawData?.rawData as AvgVideoUploadTimeRawDataType[])
            : []
        }
        columns={columns}
        loading={isFetching}
        totalItemCount={data?.getRbtVideoUploadTimeRawData?.totalRecordsCount || 0}
        defaultItemsPerPage={pageSize}
        currentPage={pageNo}
        onPageChange={(current, itemsPerPage) => {
          setPageNo(current);
          setPageSize(itemsPerPage);
        }}
      />
    </div>
  );
};
